/**
 * Norwegian working vacation days per resource for a calendar year (local dates).
 * Weekends and public holidays (YYYY-MM-DD from norwegianHolidays) excluded.
 * Overlapping vacation events dedupe by calendar day per resource.
 */

/** Firestore Timestamp, legacy _seconds, Date, or ISO string */
export const safeToDate = (timestamp) => {
  if (timestamp == null) return null;
  if (timestamp instanceof Date) return timestamp;
  if (typeof timestamp.toDate === "function") return timestamp.toDate();
  if (timestamp._seconds != null) {
    return new Date(
      timestamp._seconds * 1000 + Math.floor((timestamp._nanoseconds || 0) / 1e6)
    );
  }
  const parsed = new Date(timestamp);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const startOfLocalDay = (d) => {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
};

/** @param {Date} d */
export const formatLocalYmd = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

/**
 * @param {Array<{ date?: string }>} norwegianHolidays
 * @returns {Set<string>}
 */
export const buildHolidayYmdSet = (norwegianHolidays) => {
  const set = new Set();
  if (!Array.isArray(norwegianHolidays)) return set;
  for (const holi of norwegianHolidays) {
    if (holi && typeof holi.date === "string" && /^\d{4}-\d{2}-\d{2}$/.test(holi.date)) {
      set.add(holi.date);
    }
  }
  return set;
};

const isVacationEvent = (e) =>
  e && (e.vacation === true || e.vacation === "true" || e.vacation === 1);

const isSickEvent = (e) =>
  e && (e.sick === true || e.sick === "true" || e.sick === 1);

const isSickChildEvent = (e) =>
  e && (e.sickChild === true || e.sickChild === "true" || e.sickChild === 1);

const resourceIdsForEvent = (e) => {
  const r = e.resource;
  const list = Array.isArray(r) ? r : r != null ? [r] : [];
  return list.map((id) => String(id));
};

/**
 * @param {Array<object>} events
 * @param {number} year
 * @param {Set<string>} holidaySet
 * @param {(e: object) => boolean} isMatch
 * @returns {Map<string, Set<string>>}
 */
const workingDaySetsForYear = (events, year, holidaySet, isMatch) => {
  const result = new Map();
  const yearStart = startOfLocalDay(new Date(year, 0, 1));
  const yearEnd = startOfLocalDay(new Date(year, 11, 31));
  const list = Array.isArray(events) ? events : [];

  for (const ev of list) {
    if (!isMatch(ev)) continue;

    const rawStart = safeToDate(ev.start);
    const rawEnd = safeToDate(ev.end);
    if (!rawStart || !rawEnd) continue;

    let clipStart = startOfLocalDay(rawStart);
    let clipEnd = startOfLocalDay(rawEnd);
    if (clipStart.getTime() > clipEnd.getTime()) continue;

    if (clipEnd.getTime() < yearStart.getTime() || clipStart.getTime() > yearEnd.getTime()) {
      continue;
    }
    if (clipStart.getTime() < yearStart.getTime()) clipStart = new Date(yearStart);
    if (clipEnd.getTime() > yearEnd.getTime()) clipEnd = new Date(yearEnd);

    const ids = resourceIdsForEvent(ev);
    if (!ids.length) continue;

    const cursor = new Date(clipStart);
    const endMs = clipEnd.getTime();
    while (cursor.getTime() <= endMs) {
      const dow = cursor.getDay();
      if (dow !== 0 && dow !== 6) {
        const ymd = formatLocalYmd(cursor);
        if (!holidaySet.has(ymd)) {
          for (const rid of ids) {
            if (!result.has(rid)) result.set(rid, new Set());
            result.get(rid).add(ymd);
          }
        }
      }
      cursor.setDate(cursor.getDate() + 1);
    }
  }

  return result;
};

/**
 * @param {Array<object>} events
 * @param {number} year
 * @param {Set<string>} holidaySet
 * @returns {Map<string, Set<string>>} resourceId -> set of YYYY-MM-DD
 */
export const vacationWorkingDaySetsForYear = (events, year, holidaySet) =>
  workingDaySetsForYear(events, year, holidaySet, isVacationEvent);

/**
 * Same rules as vacation: weekdays in year, excluding public holidays, deduped per resource.
 * @param {Array<object>} events
 * @param {number} year
 * @param {Set<string>} holidaySet
 */
export const sickWorkingDaySetsForYear = (events, year, holidaySet) =>
  workingDaySetsForYear(events, year, holidaySet, isSickEvent);

/**
 * Same rules as sick: weekdays in year, excluding public holidays, deduped per resource.
 */
export const sickChildWorkingDaySetsForYear = (events, year, holidaySet) =>
  workingDaySetsForYear(events, year, holidaySet, isSickChildEvent);

/**
 * @param {Map<string, Set<string>>} daySets
 * @returns {Map<string, number>}
 */
export const mapToCounts = (daySets) => {
  const counts = new Map();
  for (const [id, set] of daySets) {
    counts.set(id, set.size);
  }
  return counts;
};

/**
 * Deep-clone resource tree; set vacationWorkingDays, sickWorkingDays, sickChildWorkingDays on leaf rows.
 * @param {Array<object>} nestedResources
 * @param {Map<string, number>} vacationCountsMap
 * @param {Map<string, number>} [sickCountsMap]
 * @param {Map<string, number>} [sickChildCountsMap]
 */
export const mergeVacationCountsIntoResources = (
  nestedResources,
  vacationCountsMap,
  sickCountsMap,
  sickChildCountsMap
) => {
  if (!Array.isArray(nestedResources)) return [];
  const sickMap = sickCountsMap instanceof Map ? sickCountsMap : null;
  const sickChildMap = sickChildCountsMap instanceof Map ? sickChildCountsMap : null;

  return nestedResources.map((node) => {
    const children = node.children;
    const hasChildren = Array.isArray(children) && children.length > 0;

    if (hasChildren) {
      return {
        ...node,
        children: mergeVacationCountsIntoResources(children, vacationCountsMap, sickCountsMap, sickChildCountsMap),
      };
    }

    const key = node.id != null ? String(node.id) : "";
    const vacationWorkingDays = vacationCountsMap.get(key) ?? 0;
    const sickWorkingDays = sickMap ? sickMap.get(key) ?? 0 : 0;
    const sickChildWorkingDays = sickChildMap ? sickChildMap.get(key) ?? 0 : 0;
    return { ...node, vacationWorkingDays, sickWorkingDays, sickChildWorkingDays };
  });
};
